import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { formatChf, formatDateShort } from '@/lib/format';
import { toast } from 'sonner';

export const Route = createFileRoute('/budgets')({
  component: BudgetsPage,
});

function BudgetsPage() {
  const qc = useQueryClient();
  const { data: budgets } = useQuery({ queryKey: ['budgets'], queryFn: api.listBudgets });
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: api.listCategories });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    categoryId: '',
    amount: '',
    period: 'monthly' as 'monthly' | 'quarterly' | 'yearly',
    alertThreshold: '0.8',
    startDate: format(new Date(), 'yyyy-MM-01'),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.createBudget({
        categoryId: form.categoryId,
        amount: Number(form.amount),
        period: form.period,
        rolloverEnabled: false,
        alertThreshold: Number(form.alertThreshold),
        startDate: form.startDate,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['budgets'] });
      toast.success('Budget créé');
      setOpen(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteBudget(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['budgets'] });
      toast.success('Budget supprimé');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Budgets</h1>
          <p className="text-sm text-muted-foreground">Surveille tes plafonds par catégorie</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" /> Nouveau budget
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau budget</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1">
                <Label>Catégorie</Label>
                <Select
                  value={form.categoryId}
                  onValueChange={(v) => setForm((f) => ({ ...f, categoryId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories ?? [])
                      .filter((c) => c.type === 'expense')
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Montant CHF</Label>
                  <Input
                    type="number"
                    step="10"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Période</Label>
                  <Select
                    value={form.period}
                    onValueChange={(v) => setForm((f) => ({ ...f, period: v as typeof form.period }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                      <SelectItem value="yearly">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Seuil alerte</Label>
                  <Input
                    type="number"
                    step="0.05"
                    min="0.1"
                    max="1.5"
                    value={form.alertThreshold}
                    onChange={(e) => setForm((f) => ({ ...f, alertThreshold: e.target.value }))}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Date début</Label>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <Button
                onClick={() => createMut.mutate()}
                disabled={!form.categoryId || !form.amount || createMut.isPending}
              >
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(budgets ?? []).map((b) => (
          <Card key={b.budget.id}>
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">{b.category.name}</CardTitle>
                <p className="text-xs text-muted-foreground">
                  {b.budget.period === 'monthly' ? 'Mensuel' : b.budget.period === 'quarterly' ? 'Trimestriel' : 'Annuel'} ·{' '}
                  {formatDateShort(b.periodStart)} → {formatDateShort(b.periodEnd)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (window.confirm('Supprimer ce budget ?')) deleteMut.mutate(b.budget.id);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-2 flex items-end justify-between">
                <span className="text-2xl font-bold num-tnum">{formatChf(b.spent)}</span>
                <span className="text-sm text-muted-foreground">/ {formatChf(b.limit)}</span>
              </div>
              <Progress
                value={Math.min(100, b.pctUsed * 100)}
                indicatorClassName={
                  b.status === 'exceeded'
                    ? 'bg-destructive'
                    : b.status === 'warning'
                      ? 'bg-warning'
                      : 'bg-success'
                }
              />
              <div className="mt-3 flex items-center justify-between text-xs">
                <Badge variant={b.status === 'exceeded' ? 'destructive' : b.status === 'warning' ? 'warning' : 'success'}>
                  {(b.pctUsed * 100).toFixed(0)} %
                </Badge>
                <span className="text-muted-foreground">{b.daysLeftInPeriod} j restants</span>
                <span className="text-muted-foreground">Proj. {formatChf(b.projectedEndOfPeriod)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {(budgets ?? []).length === 0 ? (
          <Card className="md:col-span-2 xl:col-span-3">
            <CardContent className="py-10 text-center text-muted-foreground">
              Aucun budget pour l'instant. Crée ton premier pour commencer à surveiller tes dépenses.
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
